import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/auth/AuthProvider";
import { unreadBadgeText } from "@/models/userNotification";
import { listenUnreadNotifications } from "@/services/notifications";

interface NotificationCenterContextValue {
  unreadCount: number;
  unreadCountIsCapped: boolean;
  badgeText: string | null;
}

const NotificationCenterContext = createContext<NotificationCenterContextValue | null>(null);

export function NotificationCenterProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadCountIsCapped, setUnreadCountIsCapped] = useState(false);

  useEffect(() => {
    setUnreadCount(0);
    setUnreadCountIsCapped(false);
    if (!currentUser) return;

    return listenUnreadNotifications(
      currentUser.id,
      ({ count, capped }) => {
        setUnreadCount(count);
        setUnreadCountIsCapped(capped);
      },
      () => {
        // Non-critical badge listener: the list remains available on demand.
        setUnreadCount(0);
        setUnreadCountIsCapped(false);
      },
    );
  }, [currentUser]);

  const value = useMemo<NotificationCenterContextValue>(() => ({
    unreadCount,
    unreadCountIsCapped,
    badgeText: unreadCountIsCapped ? "99+" : unreadBadgeText(unreadCount),
  }), [unreadCount, unreadCountIsCapped]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function useNotificationCenter(): NotificationCenterContextValue {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error("useNotificationCenter must be used within NotificationCenterProvider.");
  }
  return context;
}
